import ChevronRight from "@/assets/icons/ChevronRight";
import Lock from "@/assets/icons/Lock";
import Tag from "@/assets/icons/Tag";
import { HStack } from "@/components/ui/hstack";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { formatToDollars } from "@/utils/etc";
import { t } from "@transifex/native";
import type { RateMini, RateMiniAutoApplyPromotion } from "ipctg-inflow-types/InFlowAPIMisc";
import { DiscountDisplayMode, DiscountType } from "ipctg-inflow-types/InFlowEnums";
import { type PropsWithChildren, memo, useMemo } from "react";
import { Pressable } from "react-native";
import { v4 as uuidv4 } from "uuid";

interface RateSelectionProps {
  rates: RateMini[] | undefined;
  onRateSelected: (rate: RateMini) => void;
}

const RateItem = memo(
  ({
    rate,
    onSelect,
  }: {
    rate: RateMini;
    onSelect: () => void;
  }) => {
    const eligiblePromotions = useMemo(() => {
      return rate.autoApplyPromotions.filter((p) => p.userIsEligible && p.discountType !== DiscountType.None);
    }, [rate]);

    const tags = useMemo(() => {
      const tags: (RateTagProps & { copy: string | null })[] = [];
      if (rate.requiresPassphrase) {
        tags.push({ type: "passphraseProtected", copy: null });
      } else if (rate.autoApplyPromotions.length) {
        eligiblePromotions.map((p) => tags.push({ type: "promotion", copy: promotionCopy(p) }));
      }
      return tags;

      function promotionCopy(promotion: RateMiniAutoApplyPromotion) {
        if (promotion.userFacingRedeemCopy) {
          return promotion.userFacingRedeemCopy;
        }
        switch (promotion.discountType) {
          case DiscountType.Amount:
            return `${formatToDollars(promotion.discountAmount)} ${t("OFF")}`;
          case DiscountType.Percent:
            return `${promotion.discountAmount * 100}% ${t("OFF")}`;
          case DiscountType.FlatRate:
            return `${formatToDollars(promotion.discountAmount)} ${t("Flat Rate")}`;
          default:
            return t("Special promotion");
        }
      }
    }, [rate, eligiblePromotions]);

    // Not just checking for DiscountDisplayMode.nextStep so unhandled cases will fallback to the disclaimer
    const shouldShowDiscountDisclaimer = useMemo(() => {
      if (
        rate.requiresPassphrase ||
        !eligiblePromotions.length ||
        rate.discountDisplayMode === DiscountDisplayMode.none ||
        (rate.discountDisplayMode === DiscountDisplayMode.strikethroughPricing && rate.discountedAmountDue !== null)
      )
        return false;
      return true;
    }, [rate, eligiblePromotions]);

    return (
      <Pressable onPress={onSelect}>
        <HStack className="w-full flex px-4 py-[18px] min-h-[72px] items-center gap-7 rounded-xl border border-outline-300 bg-background-100">
          <HStack space="md" className="flex-1 items-center inline-block">
            <VStack space="sm">
              <Text className="font-[600] text-typography-900 text-[16px] leading-[22px] align-middle">{rate.label}</Text>
              {!!tags.length && (
                <HStack className="gap-[6px] flex-wrap">
                  {tags.map((t) => (
                    <RateTag key={uuidv4()} {...t}>
                      {t.copy}
                    </RateTag>
                  ))}
                </HStack>
              )}
              {shouldShowDiscountDisclaimer ? (
                <Text className="text-[12px] font-[400] leading-[22px] align-middle text-background-600">{t("* See final price on next page")}</Text>
              ) : (
                <></>
              )}
            </VStack>
          </HStack>
          <HStack space="md" className="items-center">
            {ratePricing()}
            <ChevronRight height={24} width={24} fill="text-outline-700" />
          </HStack>
        </HStack>
      </Pressable>
    );

    function ratePricing() {
      if (rate.requiresPassphrase) return <></>;
      if (!eligiblePromotions.length || rate.discountDisplayMode === DiscountDisplayMode.none)
        return <Text className="text-[18px] font-[600] leading-[22px] align-middle text-typography-900">{formatToDollars(rate.rateAmount, true)}</Text>;

      if (rate.discountDisplayMode === DiscountDisplayMode.strikethroughPricing && rate.discountedAmountDue !== null)
        return (
          <HStack space="sm">
            <Text className="text-[18px] font-[400] leading-[22px] align-middle text-background-600 line-through">
              {formatToDollars(rate.rateAmount, true)}
            </Text>
            <Text className="text-[18px] font-[600] leading-[22px] align-middle text-success-700">{formatToDollars(rate.discountedAmountDue, true)}</Text>
          </HStack>
        );

      return (
        <Text className="text-[18px] font-[400] leading-[22px] align-middle text-background-600">
          <span className="line-through">{formatToDollars(rate.rateAmount, true)}</span>&thinsp;*
        </Text>
      );
    }
  },
);

const RateSelection: React.FC<RateSelectionProps> = ({ rates, onRateSelected }) => {
  return (
    <VStack space="md" className="gap-[8px]">
      {rates?.map((rate) => (
        <RateItem key={rate.rateID} rate={rate} onSelect={() => onRateSelected(rate)} />
      ))}
    </VStack>
  );
};

interface RateTagProps {
  type: "passphraseProtected" | "promotion";
}

function RateTag(props: PropsWithChildren<RateTagProps>) {
  return (
    <HStack className={styling()}>
      {icon()} {copy()}
    </HStack>
  );

  function icon() {
    switch (props.type) {
      case "passphraseProtected":
        return <Lock height={16} width={16} style={{ flexShrink: 0 }} />;
      case "promotion":
        return <Tag height={16} width={16} fill="text-success-900" style={{ flexShrink: 0 }} />;
    }
  }

  function copy() {
    switch (props.type) {
      case "passphraseProtected":
        return "Passphrase protected";
      case "promotion":
        return props.children || "Special promotion";
    }
  }

  function styling() {
    switch (props.type) {
      case "passphraseProtected":
        return "px-2 py-[6px] gap-[6px] items-center rounded-md border border-outline-300 bg-background-50 text-[12px] font-[600] text-typography-900";
      case "promotion":
        return "px-2 py-[6px] gap-[6px] items-center rounded-md border border-success-200 bg-success-100 text-[12px] font-[600] text-success-900";
    }
  }
}

export default RateSelection;
